import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import { Link } from 'react-router-dom';
import { TrendingUp, Plus, Repeat, AlertCircle, CheckCircle2, X } from 'lucide-react';
import Button from '../components/ui/Button';

function CompoundModal({ investment, isOpen, onClose }: { investment: any; isOpen: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { data: info } = useQuery({
    queryKey: ['compound-info', investment.id],
    queryFn: () => api.get(`/investments/${investment.id}/compound-info`).then(r => r.data),
    enabled: isOpen,
  });

  const compoundMutation = useMutation({
    mutationFn: (data: { amount: number }) => api.post(`/investments/${investment.id}/compound`, data),
    onSuccess: () => {
      setSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['investments'] });
      queryClient.invalidateQueries({ queryKey: ['compound-info', investment.id] });
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to compound income');
    },
  });

  const eligible = parseFloat(info?.eligibleDailyIncome || '0');
  const walletBalance = parseFloat(info?.walletBalance || '0');
  const compoundAmount = parseFloat(amount) || 0;
  const currentInvAmount = parseFloat(info?.currentInvestmentAmount || investment.currentAmount || '0');
  const dailyRate = parseFloat(info?.dailyRate || investment.plan?.dailyRate || '0');
  const newInvAmount = currentInvAmount + compoundAmount;
  const newDailyIncome = newInvAmount * dailyRate / 100;
  const remainingWallet = walletBalance - compoundAmount;
  const remainingEligible = eligible - compoundAmount;

  const handleSubmit = () => {
    setError('');
    if (compoundAmount <= 0) { setError('Amount must be greater than zero'); return; }
    if (compoundAmount < 100) { setError('Minimum compound amount is ₹100'); return; }
    if (compoundAmount > eligible) { setError(`Maximum compoundable amount is ${formatCurrency(eligible)}`); return; }
    if (compoundAmount > walletBalance) { setError('Insufficient wallet balance'); return; }
    compoundMutation.mutate({ amount: compoundAmount });
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Compound Income" description={`Reinvest your daily income into ${investment.plan?.name}`}>
      {success ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-400">Compound Successful</p>
              <p className="text-xs text-slate-400 mt-0.5">Your investment amount has been increased by {formatCurrency(compoundAmount)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
              <p className="text-slate-500 text-xs mb-0.5">New Investment Amount</p>
              <p className="font-semibold text-white">{formatCurrency(newInvAmount)}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
              <p className="text-slate-500 text-xs mb-0.5">New Daily Income</p>
              <p className="font-semibold text-emerald-400">{formatCurrency(newDailyIncome)}</p>
            </div>
          </div>
          <Button onClick={handleClose} className="w-full">Done</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
              <p className="text-slate-500 text-xs mb-0.5">Eligible Daily Income</p>
              <p className="font-semibold text-emerald-400">{formatCurrency(eligible)}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
              <p className="text-slate-500 text-xs mb-0.5">Wallet Balance</p>
              <p className="font-semibold text-white">{formatCurrency(walletBalance)}</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Amount to Reinvest</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">₹</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(''); }}
                placeholder="0.00"
                min="100"
                max={eligible}
                step="1"
                className="w-full pl-7 pr-4 py-2.5 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-all"
              />
            </div>
          </div>

          {compoundAmount > 0 && (
            <div className="p-3 rounded-xl bg-primary-600/5 border border-primary-500/10 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">New Investment Amount</span>
                <span className="text-white font-medium">{formatCurrency(newInvAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">New Daily Income (approx)</span>
                <span className="text-emerald-400 font-medium">{formatCurrency(newDailyIncome)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Remaining Wallet</span>
                <span className="text-white font-medium">{formatCurrency(Math.max(remainingWallet, 0))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Remaining Eligible Income</span>
                <span className="text-white font-medium">{formatCurrency(Math.max(remainingEligible, 0))}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} className="flex-1">Cancel</Button>
            <Button
              onClick={handleSubmit}
              isLoading={compoundMutation.isPending}
              disabled={compoundAmount <= 0 || compoundAmount < 100}
              className="flex-1"
            >
              Confirm Compound
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function InvestmentsPage() {
  const [compoundInvestment, setCompoundInvestment] = useState<any>(null);

  const { data: investments } = useQuery({
    queryKey: ['investments'],
    queryFn: () => api.get('/investments').then(r => r.data),
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="d-page-header">
          <h1 className="text-2xl font-bold text-white">My Investments</h1>
          <p className="text-slate-400 mt-1">Track your portfolio performance.</p>
        </div>
        <Link to="/investments/plans"><Button><Plus className="w-4 h-4" />New Investment</Button></Link>
      </div>

      {!investments || investments.length === 0 ? (
        <Card>
          <EmptyState
            icon={TrendingUp}
            title="No Investments"
            description="Start investing to grow your portfolio"
            action={<Link to="/investments/plans"><Button>View Plans</Button></Link>}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {investments.map((inv: any) => {
            const progress = inv.plan?.duration ? (inv.daysCompleted / inv.plan.duration) * 100 : 0;
            const canCompound = inv.status === 'ACTIVE' && inv.plan?.compoundingEnabled;
            const totalCompound = parseFloat(inv.totalCompoundAmount || '0');
            return (
              <div key={inv.id} className="d-card-hover p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{inv.plan?.name}</h3>
                      <p className="text-xs text-slate-500">Started {new Date(inv.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <Badge status={inv.status} />
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div>
                    <p className="text-slate-500 text-xs mb-0.5">Principal</p>
                    <p className="font-semibold text-white">{formatCurrency(inv.principalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-0.5">Current Amount</p>
                    <p className="font-semibold text-white">{formatCurrency(inv.currentAmount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-0.5">Total Earned</p>
                    <p className="font-semibold text-emerald-400">{formatCurrency(inv.totalEarned)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-0.5">Daily Rate</p>
                    <p className="font-semibold text-white">{inv.plan?.dailyRate}%</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-0.5">Days</p>
                    <p className="font-semibold text-white">{inv.daysCompleted} / {inv.plan?.duration}</p>
                  </div>
                  {totalCompound > 0 && (
                    <div>
                      <p className="text-slate-500 text-xs mb-0.5">Compounded</p>
                      <p className="font-semibold text-teal-400">{formatCurrency(totalCompound)}</p>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-500">Progress</span>
                    <span className="text-slate-400">{Math.round(progress)}%</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary-600 to-accent rounded-full transition-all duration-500" style={{ width: `${Math.min(progress, 100)}%` }} />
                  </div>
                </div>

                {canCompound && (
                  <button
                    onClick={() => setCompoundInvestment(inv)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600/10 border border-teal-500/20 text-teal-400 text-sm font-medium hover:bg-teal-600/20 transition-colors"
                  >
                    <Repeat className="w-4 h-4" />
                    Compound Income
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {compoundInvestment && (
        <CompoundModal
          investment={compoundInvestment}
          isOpen={true}
          onClose={() => setCompoundInvestment(null)}
        />
      )}
    </div>
  );
}
